import { useState, FormEvent, useEffect } from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { onboardingApi } from '../api/client'
import { Alert, Button, Card, Field, Input } from '../components/ui'

export default function SetPasswordPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token') ?? ''

  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [done, setDone] = useState(false)

  useEffect(() => {
    if (!token) {
      setError(t('setPassword.noToken'))
    }
  }, [token, t])

  // Cleared on unmount: without the clearTimeout the redirect fires into an
  // unmounted router (and, in tests, into a torn-down environment).
  useEffect(() => {
    if (!done) return
    const id = setTimeout(() => navigate('/login'), 3000)
    return () => clearTimeout(id)
  }, [done, navigate])

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (loading) return
    setError('')

    if (password.length < 8) {
      setError(t('setPassword.tooShort'))
      return
    }
    if (password !== confirm) {
      setError(t('setPassword.mismatch'))
      return
    }

    setLoading(true)
    try {
      await onboardingApi.setPassword(token, password)
      setDone(true)
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error
      if (msg === 'invalid or expired token') {
        setError(t('setPassword.expiredToken'))
      } else {
        setError(t('setPassword.error'))
      }
    } finally {
      setLoading(false)
    }
  }

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="w-full max-w-sm text-center">
          <Card padded={false} className="p-8">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-lg font-bold text-gray-900 mb-2">{t('setPassword.successTitle')}</h2>
            <p className="text-sm text-gray-500">{t('setPassword.successHint')}</p>
          </Card>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-gray-900">{t('setPassword.title')}</h1>
          <p className="text-gray-500 text-sm mt-1">{t('setPassword.subtitle')}</p>
        </div>

        <Card>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && <Alert tone="danger">{error}</Alert>}

            {!token ? (
              <p className="text-sm text-gray-500 text-center">
                {t('setPassword.noToken')}{' '}
                <Link to="/forgot-password" className="text-brand-600 hover:text-brand-700 font-medium">
                  {t('setPassword.requestNew')}
                </Link>
              </p>
            ) : (
              <>
                <Field label={t('setPassword.password')} hint={t('setPassword.minLength')}>
                  {(p) => (
                    <Input
                      {...p}
                      type="password"
                      required
                      minLength={8}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      autoComplete="new-password"
                    />
                  )}
                </Field>

                <Field label={t('setPassword.confirm')}>
                  {(p) => (
                    <Input
                      {...p}
                      type="password"
                      required
                      value={confirm}
                      onChange={(e) => setConfirm(e.target.value)}
                      autoComplete="new-password"
                    />
                  )}
                </Field>

                <Button type="submit" loading={loading} fullWidth>
                  {loading ? t('setPassword.submitting') : t('setPassword.submit')}
                </Button>
              </>
            )}
          </form>
        </Card>
      </div>
    </div>
  )
}
